import { useQuery } from '@tanstack/react-query';
import { QUERY_KEY_MAP } from 'src/constants';
import { useSearch } from 'src/hooks';
import { REQUEST_TYPE_MAP } from 'src/utils/helpers/get-request-type';
import { listStoresPromise } from 'src/utils/api/stores/listStoresPromise';
import { useParams } from 'react-router-dom';
import { retrieveStorePromise } from 'src/utils/api/stores/retrieveStorePromise';
import { isEmpty } from 'lodash';

export interface UseListStoresParams {
  shouldUseSearchParams?: boolean;
}
/** Hook used to get a list of stores. shouldUseSearchParams should be set to true if we want to get a paginated list of stores.
 *  Change shouldUseSearchParams to false if you want to get every available store.
 */
export const useListStores = ({ shouldUseSearchParams }: UseListStoresParams) => {
  const [searchParams] = useSearch();

  const baseRequestParams = { request_type: REQUEST_TYPE_MAP.GET };

  const addSearchParams = shouldUseSearchParams
    ? {
        ...baseRequestParams,
        search_params: {
          page_num: searchParams?.page ?? 1,
          search_term: searchParams?.search ?? '',
          page_size: 25,
        },
      }
    : baseRequestParams;

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: [QUERY_KEY_MAP.useListStores, addSearchParams],
    queryFn: () => listStoresPromise(addSearchParams),
  });

  return { data, isLoading, isFetching, error };
};

/**Hook used to get a list of stores based query params e.g. org_id */
export const useGetOrgStores = (orgId: string) => {
  const queryParams = { search_params: { org_id: orgId }, request_type: REQUEST_TYPE_MAP.GET };

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: [QUERY_KEY_MAP.useListStores, queryParams],
    queryFn: () => listStoresPromise(queryParams),
    enabled: !isEmpty(orgId),
  });

  return { data, isLoading, isFetching, error };
};

export const useGetStore = (uuid?: string, customIsEnabled?: boolean) => {
  const { storeId } = useParams<{ storeId: string }>();

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: [QUERY_KEY_MAP.useGetStore, storeId || uuid],
    queryFn: () =>
      retrieveStorePromise({
        request_type: REQUEST_TYPE_MAP.GET,
        store_id: uuid || storeId,
      }),
    enabled: typeof customIsEnabled === 'boolean' ? customIsEnabled : true,
  });

  return { data, isLoading, isFetching, error };
};
